import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const ItemModal = ({
  open,
  onClose,
  onSave,
  itemData = null,
  mode = 'create', // 'create', 'edit', 'view'
  categories = [],
  brands = [],
  uoms = [],
  existingItems = [] // Add this prop to receive the list of existing items
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    Name: '',
    BarCode: '',
    Code: '',
    HSNCode: '',
    CategoryId: '',
    BrandId: '',
    UOMId: '',
    VATRate: '',
    MRP: '',
    PurchaseRate: '',
    SaleRate: '',
    MinRequiredQty: '',
    AlertLevelStock: '',
    IsProduct: false,
    IsReturnable: false,
    ReturnDays: '',
    HasWarranty: false,
    WarrantyDays: ''
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'itemData:', itemData);

      if (itemData && mode !== 'create') {
        console.log('Loading existing item data for edit/view, ID:', itemData.Id);
        console.log('CategoryId:', itemData.CategoryId, 'BrandId:', itemData.BrandId, 'UOMId:', itemData.UOMId);
        
        // If CategoryId is not present but CategoryName is, find the ID from categories array
        let categoryId = itemData.CategoryId || '';
        if (!categoryId && itemData.CategoryName) {
          const matchedCategory = categories.find(cat => cat.Name === itemData.CategoryName);
          if (matchedCategory) {
            categoryId = matchedCategory.Id;
            console.log('Matched CategoryName to CategoryId:', categoryId);
          }
        }

        // If BrandId is not present but BrandName is, find the ID from brands array
        let brandId = itemData.BrandId || '';
        if (!brandId && itemData.BrandName) {
          const matchedBrand = brands.find(brand => brand.Name === itemData.BrandName);
          if (matchedBrand) {
            brandId = matchedBrand.Id;
            console.log('Matched BrandName to BrandId:', brandId);
          }
        }

        // If UOMId is not present but UOMName is, find the ID from uoms array
        let uomId = itemData.UOMId || '';
        if (!uomId && itemData.UOMName) {
          const matchedUOM = uoms.find(uom => uom.Name === itemData.UOMName);
          if (matchedUOM) {
            uomId = matchedUOM.Id;
            console.log('Matched UOMName to UOMId:', uomId);
          }
        }
        
        setFormData({
          Id: itemData.Id || '',
          Name: itemData.Name || '',
          BarCode: itemData.BarCode || '',
          Code: itemData.Code || '',
          HSNCode: itemData.HSNCode || '',
          CategoryId: categoryId,
          BrandId: brandId,
          UOMId: uomId,
          VATRate: itemData.VATRate || 0,
          MRP: itemData.MRP || 0,
          PurchaseRate: itemData.PurchaseRate || 0,
          SaleRate: itemData.SaleRate || 0,
          MinRequiredQty: itemData.MinRequiredQty || 0,
          AlertLevelStock: itemData.AlertLevelStock || 0,
          IsProduct: itemData.IsProduct || false,
          IsReturnable: itemData.IsReturnable || false,
          ReturnDays: itemData.ReturnDays || 0,
          HasWarranty: itemData.HasWarranty || false,
          WarrantyDays: itemData.WarrantyDays || 0
        });
      } else {
        console.log('Creating new item');
        // Reset form for create mode
        setFormData({
          Name: '',
          BarCode: '',
          Code: '',
          HSNCode: '',
          CategoryId: '',
          BrandId: '',
          UOMId: '',
          VATRate: '',
          MRP: '',
          PurchaseRate: '',
          SaleRate: '',
          MinRequiredQty: '',
          AlertLevelStock: '',
          IsProduct: false,
          IsReturnable: false,
          ReturnDays: '',
          HasWarranty: false,
          WarrantyDays: ''
        });
      }
      setErrors({});
    }
  }, [open, itemData, mode, categories, brands, uoms]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }

    // Validate HSN Code - only allow digits
    if (name === 'HSNCode' && value) {
      const hsnPattern = /^[0-9]*$/;
      if (!hsnPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }

    // Handle category selection - auto-populate HSN Code and VAT Rate
    if (name === 'CategoryId' && value) {
      const selectedCategory = categories.find(cat => cat.Id === parseInt(value));
      if (selectedCategory) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          HSNCode: selectedCategory.HSNCode || prev.HSNCode,
          VATRate: selectedCategory.VATRate || prev.VATRate
        }));

        // Clear related errors
        setErrors(prev => ({
          ...prev,
          [name]: '',
          HSNCode: selectedCategory.HSNCode ? '' : prev.HSNCode,
          VATRate: selectedCategory.VATRate ? '' : prev.VATRate
        }));
        return;
      }
    }

    // Handle numeric inputs
    if (['VATRate', 'MinRequiredQty', 'AlertLevelStock', 'MRP', 'PurchaseRate', 'SaleRate', 'ReturnDays', 'WarrantyDays'].includes(name)) {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name.trim()) {
      newErrors.Name = 'Item name is required';
    }

    if (!formData.CategoryId) {
      newErrors.CategoryId = 'Category is required';
    }

    if (!formData.VATRate || formData.VATRate === '') {
      newErrors.VATRate = 'VAT Rate is required';
    } else {
      const vatRate = parseFloat(formData.VATRate);
      if (isNaN(vatRate) || vatRate < 0 || vatRate > 100) {
        newErrors.VATRate = 'VAT Rate must be between 0 and 100';
      }
    }

    if (!formData.HSNCode.trim()) {
      newErrors.HSNCode = 'HSN Code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateItem = () => {
    // Check if an item with the same name exists in the list (case-insensitive)
    const duplicate = existingItems.find(item => {
      // Skip the current item in edit mode
      if (mode === 'edit' && itemData && item.Id === itemData.Id) {
        return false;
      }
      
      // Check if name matches (case-insensitive)
      const nameMatch = item.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
      
      return nameMatch;
    });
    
    return duplicate;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for duplicate item
    const duplicate = checkDuplicateItem();
    if (duplicate) {
      setDuplicateMessage(`An item with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        BarCode: formData.BarCode || null,
        Code: formData.Code || null,
        HSNCode: formData.HSNCode,
        CategoryId: parseInt(formData.CategoryId) || null,
        BrandId: parseInt(formData.BrandId) || null,
        UOMId: parseInt(formData.UOMId) || null,
        VATRate: parseFloat(formData.VATRate) || 0,
        MRP: parseFloat(formData.MRP) || 0,
        PurchaseRate: parseFloat(formData.PurchaseRate) || 0,
        SaleRate: parseFloat(formData.SaleRate) || 0,
        MinRequiredQty: parseFloat(formData.MinRequiredQty) || 0,
        AlertLevelStock: parseFloat(formData.AlertLevelStock) || 0,
        IsProduct: formData.IsProduct || false,
        IsReturnable: formData.IsReturnable || false,
        ReturnDays: parseInt(formData.ReturnDays) || 0,
        HasWarranty: formData.HasWarranty || false,
        WarrantyDays: parseInt(formData.WarrantyDays) || 0,
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        // Validate that we have the item ID
        if (!itemData || !itemData.Id) {
          console.error('Cannot update: Missing item ID');
          alert('Error: Cannot update item - missing ID');
          return;
        }
        dataToSend.Id = itemData.Id;
        // For edit mode, add modification fields but don't include ID in body (it's in URL)
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        // Keep original creation data if available
        if (itemData.CreatedBy) dataToSend.CreatedBy = itemData.CreatedBy;
        if (itemData.CreatedDate) dataToSend.CreatedDate = itemData.CreatedDate;

        console.log('Updating item with ID:', itemData.Id);
        console.log('Update data being sent:', dataToSend);
        console.log('API call: PUT /ItemApi/' + itemData.Id);

        const response = await updateData('ItemApi', itemData.Id, dataToSend, token);
        console.log('Update response:', response);
      } else {
        // For create mode, add creation fields
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();

        console.log('Creating new item');
        console.log('Create data being sent:', dataToSend);
        console.log('API call: POST /ItemApi');

        const response = await postData('ItemApi', dataToSend, token);
        console.log('Create response:', response);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Item';
      case 'edit': return 'Edit Item';
      case 'view': return 'Item Details';
      default: return 'Item';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0' : '12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '2px',
          margin: isMobile ? '0' : '8px'
        }
      }}
    >
      <Box style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '10px',
        overflow: 'hidden',
        height: isMobile ? '100vh' : 'auto'
      }}>
        <DialogTitle style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'auto'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <InventoryIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
              {getTitle()}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            style={{ color: 'white', padding: '2px' }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{
          padding: '12px 16px',
          paddingBottom: '8px'
        }}>
          <Grid container spacing={1.5}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '6px',
                color: '#764ba2',
                fontSize: '13px'
              }}>
                Basic Information
              </Typography>
            </Grid>

            {/* Item Name */}
            <Grid item xs={12} sm={8}>
              <TextField
                name="Name"
                label="Item Name *"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Name}
                helperText={errors.Name}
                disabled={isReadOnly}
                placeholder="Enter item name"
                inputProps={{ maxLength: 150 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Bar Code and Code */}
            <Grid item xs={12} sm={2}>
              <TextField
                name="BarCode"
                label="Bar Code"
                value={formData.BarCode}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter bar code"
                inputProps={{ maxLength: 50 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                name="Code"
                label="Code"
                value={formData.Code}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter code"
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* HSN Code */}
            <Grid item xs={12} sm={3}>
              <TextField
                name="HSNCode"
                label="HSN Code *"
                value={formData.HSNCode}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.HSNCode}
                helperText={errors.HSNCode}
                disabled={isReadOnly}
                placeholder="Enter HSN code"
                inputProps={{ maxLength: 10 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Category, Brand, UOM */}
            <Grid item xs={12} sm={3}>
              <TextField
                select
                name="CategoryId"
                label="Category *"
                value={formData.CategoryId || ''}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.CategoryId}
                helperText={errors.CategoryId}
                disabled={isReadOnly}
                SelectProps={{ native: true }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              >
                <option value=""></option>
                {categories.map(category => (
                  <option key={category.Id} value={category.Id}>{category.Name}</option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                select
                name="BrandId"
                label="Brand"
                value={formData.BrandId || ''}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                SelectProps={{ native: true }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              >
                <option value=""></option>
                {brands.map(brand => (
                  <option key={brand.Id} value={brand.Id}>{brand.Name}</option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                select
                name="UOMId"
                label="UOM"
                value={formData.UOMId || ''}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                SelectProps={{ native: true }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              >
                <option value=""></option>
                {uoms.map(uom => (
                  <option key={uom.Id} value={uom.Id}>{uom.Name}</option>
                ))}
              </TextField>
            </Grid>

            {/* Pricing & Tax Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '6px',
                marginTop: '4px',
                color: '#764ba2',
                fontSize: '13px'
              }}>
                Pricing & Tax
              </Typography>
            </Grid>

            {/* MRP, Purchase Rate, Sale Rate - All in one row */}
            <Grid item xs={12} sm={2}>
              <TextField
                name="MRP"
                label="MRP (i.-)"
                value={formData.MRP}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="0.00"
                InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                name="PurchaseRate"
                label="Purchase Rate"
                value={formData.PurchaseRate}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="0.00"
                InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                name="SaleRate"
                label="Sale Rate"
                value={formData.SaleRate}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="0.00"
                InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* VAT Rate, Min Required Qty - All in one row */}
            <Grid item xs={12} sm={2}>
              <TextField
                name="VATRate"
                label="VAT Rate (%) *"
                value={formData.VATRate}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.VATRate}
                helperText={errors.VATRate}
                disabled={isReadOnly}
                placeholder="0"
                InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                name="MinRequiredQty"
                label="Min Required Qty"
                value={formData.MinRequiredQty}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="0"
                InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* Alert Level Stock - Full width */}
            <Grid item xs={12} sm={2}>
              <TextField
                name="AlertLevelStock"
                label="Alert Level Stock"
                value={formData.AlertLevelStock}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="0"
                InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* Toggles Section */}
            <Grid item xs={12}>
              <Box style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.IsProduct}
                      onChange={handleChange}
                      name="IsProduct"
                      disabled={isReadOnly}
                      size="small"
                    />
                  }
                  label={<span style={{ fontSize: '12px' }}>Is Product</span>}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.IsReturnable}
                      onChange={handleChange}
                      name="IsReturnable"
                      disabled={isReadOnly}
                      size="small"
                    />
                  }
                  label={<span style={{ fontSize: '12px' }}>Returnable</span>}
                />

                {formData.IsReturnable && (
                  <TextField
                    name="ReturnDays"
                    label="Return Days"
                    value={formData.ReturnDays}
                    onChange={handleChange}
                    size="small"
                    disabled={isReadOnly}
                    placeholder="0"
                    style={{ width: '120px' }}
                    InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                    InputLabelProps={{ style: labelStyle }}
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.HasWarranty}
                      onChange={handleChange}
                      name="HasWarranty"
                      disabled={isReadOnly}
                      size="small"
                    />
                  }
                  label={<span style={{ fontSize: '12px' }}>Has Warranty</span>}
                />

                {formData.HasWarranty && (
                  <TextField
                    name="WarrantyDays"
                    label="Warranty Days"
                    value={formData.WarrantyDays}
                    onChange={handleChange}
                    size="small"
                    disabled={isReadOnly}
                    placeholder="0"
                    style={{ width: '120px' }}
                    InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                    InputLabelProps={{ style: labelStyle }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{
          padding: '8px 12px',
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          gap: '6px',
          minHeight: 'auto'
        }}>
          <Button
            onClick={onClose}
            style={{
              color: '#6c757d',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '12px',
              padding: '4px 8px'
            }}
          >
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textTransform: 'none',
                fontWeight: '600',
                borderRadius: '4px',
                padding: '4px 12px',
                fontSize: '12px'
              }}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Add Item' : 'Update Item'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    {/* Duplicate Warning Dialog */}
    <Dialog
      open={duplicateDialogOpen}
      onClose={() => setDuplicateDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
        <WarningAmberIcon 
          style={{ 
            fontSize: '64px', 
            color: '#ff9800',
            marginBottom: '16px'
          }} 
        />
        <Typography 
          variant="h6" 
          style={{ 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#2d3748',
            fontSize: '16px'
          }}
        >
          Duplicate Item Found
        </Typography>
        <Typography 
          style={{ 
            color: '#666',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {duplicateMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
        <Button
          onClick={() => setDuplicateDialogOpen(false)}
          variant="contained"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textTransform: 'none',
            fontWeight: '600',
            borderRadius: '8px',
            padding: '8px 32px',
            fontSize: '14px'
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default ItemModal;